/**
 * Which day of the seeker's Journal a thing belongs to.
 *
 * Two clocks feed the Journal and they disagree every evening west of UTC:
 * readings carry a full timestamp (grouped by the seeker's local day), while
 * drops carry a bare date stamped by the server, which runs on UTC. At 8:26pm
 * Pacific on Aug 7 the server's date is already Aug 8, so a drop the seeker had
 * just seen was filed under tomorrow — and tomorrow sorts above Today. The
 * Journal opened on an empty future day with the just-finished reading pushed
 * beneath it.
 *
 * The seeker's own clock is the one that matters here, and only the client
 * knows it. So the correction lives here: nothing in a Journal may be dated
 * after the seeker's today.
 */

const p = (n) => String(n).padStart(2, '0')

/** The seeker's local day, in group-key shape. */
export function todayKey(now = new Date()) {
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`
}

/** Compare two YYYY-M-D keys as dates — string order breaks on '2026-9-01'. */
function isAfter(key, ref) {
  const a = key.split('-').map(Number)
  const b = ref.split('-').map(Number)
  for (let i = 0; i < 3; i++) {
    if (a[i] !== b[i]) return a[i] > b[i]
  }
  return false
}

/**
 * The day key for a reading timestamp or a drop date, clamped to today.
 *
 * @param iso    a full ISO timestamp, a bare 'YYYY-MM-DD', or nothing
 * @param today  the seeker's local day (defaults to now)
 */
export function journalDayKey(iso, today = todayKey()) {
  let key
  if (typeof iso === 'string') {
    // A bare date is already a calendar day and must be used as written.
    // new Date('2026-05-14') is UTC midnight, which slides back a day west of
    // UTC and once mis-filed every drop onto the previous day.
    const m = iso.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
    if (m) key = `${m[1]}-${p(Number(m[2]))}-${p(Number(m[3]))}`
  }
  if (!key) {
    const d = iso ? new Date(iso) : new Date()
    if (isNaN(d.getTime())) return today
    key = todayKey(d)
  }
  // Nothing in a seeker's Journal happens after the seeker's today. A server
  // date that has already rolled over belongs to the day they actually saw it.
  return isAfter(key, today) ? today : key
}
